// ** React Imports
import { FC, ReactNode, Suspense } from 'react';
import { Navigate } from 'react-router-dom';

// ** Utils
import { Page } from './types';
import { RootState } from 'reduxStore/store';
import { useSelector } from 'react-redux';
import { PENDING_REDIRECT_KEY } from 'hooks/useAuthSession';

/**
 * Where to send a visitor who is already signed in.
 *
 * Normally the home page. But /login and /signup live behind this wrapper, so
 * the instant a sign-in lands in the store this component re-renders -- while
 * still mounted for the page being left -- and its <Navigate> pushes from a
 * mount effect, beating the navigate() inside useAuthSession. That silently
 * stranded brand new customers on the home page instead of card activation.
 *
 * useAuthSession parks its intended destination under PENDING_REDIRECT_KEY
 * before dispatching, so whichever navigation wins the race, both land in the
 * same place.
 */
/** A parked destination older than this is stale and ignored. */
const PENDING_REDIRECT_TTL_MS = 15000;

const postAuthTarget = (): string => {
    try {
        const raw = sessionStorage.getItem(PENDING_REDIRECT_KEY);
        if (raw) {
            sessionStorage.removeItem(PENDING_REDIRECT_KEY);
            const { path, ts } = JSON.parse(raw) as { path?: string; ts?: number };
            if (path && typeof ts === 'number' && Date.now() - ts < PENDING_REDIRECT_TTL_MS) return path;
        }
    } catch (err) {
        // Private mode, blocked storage, or a malformed entry - the home page
        // is always a safe place to land.
    }
    return '/';
};

export interface PublicRouteProps {
    children?: ReactNode;
    route: Page;
}

const PublicRoute: FC<PublicRouteProps> = ({ children, route }) => {
    // Hooks must not sit behind a condition - `route` was guarding this call,
    // so a route rendered without one would change the hook order and crash.
    const user = useSelector((state: RootState) => state.auth.accessToken);

    if (route && user) {
        return <Navigate to={postAuthTarget()} replace />;
    }

    return <Suspense fallback={null}>{children}</Suspense>;
};

export default PublicRoute;
